import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useModal } from 'mui-modal-provider';
import { useSnackbar } from 'notistack';

import { venueActionAccessRequestApi } from '../../../api/accessRequest/venueActionAccessRequestApi';
import { useReservationLimitInPeriodContext } from '../../../contexts/ReservationsLimitInPeriodContext';
import { VenueAccessREnum, VenueActionAccessRequest } from '../../../graphql/API';
import { extractSubscriptionPlan } from '../../../pages/SubscriptionPlans/common/helper';
import { fetchVenue } from '../../../redux/features/venue/venueSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../../utils/venue-tags';
import { BrandLimitReachedPopup } from '../../Dialogs/BrandLimitReached/BrandLimitReachedPopup';

export type OnAction = (input: VenueActionAccessRequest | undefined) => void;

export const useAction = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { subscription } = useAppSelector((state) => state.subscription.value);
  const venueState = useAppSelector((state) => state.venue.value);
  const { showModal } = useModal();
  const { getReservationsLimitInPeriodHandle } = useReservationLimitInPeriodContext() || {};
  const appDispatch = useAppDispatch();
  const [reservationID, setReservationID] = React.useState<string | null>(null);

  const [loadingState, setLoading] = React.useState<Record<string, boolean>>({});
  const [userID, setUserID] = React.useState<number>();
  const [successState, setSuccessState] = React.useState(false);

  const queryClient = useQueryClient();

  const subscriptionPlan = React.useMemo(() => {
    const [plan] = extractSubscriptionPlan(subscription?.sku ?? '');

    return plan;
  }, [subscription]);

  const isBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans);

  const onAction = React.useCallback<OnAction>(
    async (input) => {
      if (isBrand && !subscriptionPlan) {
        return navigate('/plans');
      }
      if (input) {
        const key = `${input.userID}:${input.offerID}:${input.action}`;
        try {
          setLoading((x) => ({ ...x, [key]: true }));

          const response = await venueActionAccessRequestApi(input);

          if (response.reservationID) setReservationID(response.reservationID);

          await queryClient.invalidateQueries({ queryKey: ['list-access-request'] });
          if (getReservationsLimitInPeriodHandle) getReservationsLimitInPeriodHandle();
          if (input.action === VenueAccessREnum.accepted) {
            appDispatch(fetchVenue());
          }
          setUserID(input.userID);
          setSuccessState(true);
          setLoading((x) => ({ ...x, [key]: false }));
        } catch (error) {
          const _error = error as Error;
          if (_error.message.includes('limit')) {
            showModal(BrandLimitReachedPopup, {});
          }
          enqueueSnackbar(_error.message, {
            variant: 'error',
          });
          setUserID(input.userID);
          setSuccessState(false);
          setLoading((x) => ({ ...x, [key]: false }));
        }
      }
    },
    [isBrand, navigate, queryClient, showModal, subscriptionPlan, enqueueSnackbar, setReservationID, appDispatch, getReservationsLimitInPeriodHandle],
  );

  return { loadingState, onAction, successState, setSuccessState, userID, reservationID };
};
